import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Briefcase, Upload, FileText,
  Compass, Mail, Search, Copy, Download, Edit2, Trash2, Plus
} from 'lucide-react';

const Linkedin = ({ size = 16, style }) => (
  <svg 
    width={size} 
    height={size} 
    style={style} 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.779-1.75-1.75s.784-1.75 1.75-1.75 1.75.779 1.75 1.75-.784 1.75-1.75 1.75zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
  </svg>
);

const Chrome = ({ size = 16, style }) => (
  <svg 
    width={size} 
    height={size} 
    style={style} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="4" />
    <line x1="21.17" y1="8" x2="12" y2="8" />
    <line x1="3.95" y1="6.06" x2="8.54" y2="14" />
    <line x1="10.88" y1="21.94" x2="15.46" y2="14" />
  </svg>
);

export default function Dashboard({
  resumeData,
  variants = [],
  coverLetters = [],
  activeTab,
  onTabChange,
  onOpenCustomize,
  isUploadingResume,
  handleResumeUpload,
  resumeFileInputRef,
  credits,
  user,
  theme = 'light',
  onStartManual,
  onSelectVariant,
  atsScore,
  handleDeleteVariant,
  authToken,
  onCreateNew,       // opens CreateMenu wizard with given step
  onImportResume,    // triggers ImportChoiceModal
  onDeleteCoverLetter, // AppContext-aware CL delete
  setEntryIntent,    // Sets entry_intent on session before chat navigation
}) {
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  // BUG-S15 fix: removed hardcoded developer name fallback
  const userName = user?.name || user?.email?.split('@')[0] || 'there';
  
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredCardId, setHoveredCardId] = useState(null);
  const [hoveredCoverId, setHoveredCoverId] = useState(null);
  const [startingAI, setStartingAI] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const mostRecentDoc = useMemo(() => {
    if (!variants || variants.length === 0) return null;
    return [...variants].sort((a, b) => new Date(b.updatedAt || b.updated_at) - new Date(a.updatedAt || a.updated_at))[0];
  }, [variants]);

  const filteredVariants = useMemo(() => {
    if (!variants) return [];
    return variants.filter(v => 
      (v.name || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [variants, searchQuery]);

  const handleStartAI = async () => {
    setStartingAI(true);
    try {
      const res = await fetch('/api/chat/sessions/startup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          workspace_ctx: {
            document_type: 'resume',
            document_id: 'master',
          },
        }),
      });

      if (res.ok) {
        const data = await res.json();
        const sid = data.session_id;
        if (sid) {
          navigate(`/app/resume/master/chat?session=${sid}&startup=1`);
          return;
        }
      }
    } catch (err) {
      console.warn('[Dashboard] Startup session failed, falling back:', err);
    } finally {
      setStartingAI(false);
    }
    // Fallback: navigate without startup session
    navigate('/app/resume/master/chat');
  };

  const handleStartScratch = () => {
    if (onStartManual) {
      onStartManual();
    } else {
      navigate('/app/resume/master/editor');
    }
  };

  const handleDuplicate = async (v, e) => {
    e.stopPropagation();
    if (!v) return;
    try {
      const isMaster = v.isMaster || v.is_master || v.id === 'master';
      const sourceOverrides = isMaster ? {} : (v.overrides || {});
      const sourceName = v.name || 'Master Resume';

      const res = await fetch('/api/variants', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          name: `${sourceName} Copy`,
          variant_type: v.variant_type || 'document',
          overrides: sourceOverrides,
          template_id: v.template_id || null,
          accent_color: v.accent_color || null
        })
      });

      if (res.ok) {
        const data = await res.json();
        if (data.variant?.id) {
          onSelectVariant(data.variant.id);
          navigate(`/app/resume/${data.variant.id}/editor`);
        }
      }
    } catch (err) {
      console.error("Error duplicating variant:", err);
    }
  };

  const handleDownloadClick = (vId, e) => {
    e.stopPropagation();
    onSelectVariant(vId);
    setTimeout(() => {
      navigate(`/app/resume/${vId}/chat`);
    }, 150);
  };

  const handleEditClick = (vId, e) => {
    e.stopPropagation();
    onSelectVariant(vId);
    navigate(`/app/resume/${vId}/editor`);
  };

  // Entry Point Routing: "Continue" — user is updating/improving existing resume
  const handleContinueChat = (vId, e) => {
    e.stopPropagation();
    if (setEntryIntent) setEntryIntent('continue');
    onSelectVariant(vId);
    navigate(`/app/resume/${vId}/chat`);
  };

  // Entry Point Routing: "Tailor for Job" — user has a JD to match against
  const handleTailorJD = (vId, e) => {
    e.stopPropagation();
    if (setEntryIntent) setEntryIntent('tailor_jd');
    onSelectVariant(vId);
    navigate(`/app/resume/${vId}/chat`);
  };

  const handleDeleteClick = (vId, e) => {
    e.stopPropagation();
    if (handleDeleteVariant) {
      handleDeleteVariant(vId);
    }
  };

  const handleEditCoverLetter = (clId, e) => {
    e.stopPropagation();
    navigate(`/app/cover-letter/${clId}`);
  };

  const handleDeleteCoverLetter = async (clId, e) => {
    e.stopPropagation();
    // BUG-S08 fix: was calling wrong API URL (/api/cover-letters plural).
    // BUG-S17 fix: was using window.location.reload().
    // Now delegates to AppContext-aware handler which uses correct URL + updates state.
    if (onDeleteCoverLetter) {
      onDeleteCoverLetter(clId);
    } else {
      // Fallback: correct singular URL
      try {
        await fetch(`/api/cover-letter/${clId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${authToken}` }
        });
      } catch (err) {
        console.error(err);
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Recently';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'Recently';
    }
  };

  // Helper to compile final variant resume data
  const getVariantResumeData = (v) => {
    const base = resumeData || {};
    const overrides = v?.overrides || {};
    return {
      personal: {
        name: overrides.personal?.name || base.personal?.name || userName || 'Your Name',
        title: overrides.personal?.title || base.personal?.title || 'Professional Title',
        email: overrides.personal?.email || base.personal?.email || '',
        phone: overrides.personal?.phone || base.personal?.phone || '',
      },
      work: overrides.work !== undefined ? overrides.work : (base.work || []),
      education: overrides.education !== undefined ? overrides.education : (base.education || []),
      skills: overrides.skills !== undefined ? overrides.skills : (base.skills || []),
    };
  };

  // Renders the mini sheet of paper inside preview card using actual content
  const renderMiniResumePreview = (v, isHovered) => {
    const data = getVariantResumeData(v);
    const name = data.personal.name;
    const title = data.personal.title;
    const workItems = (data.work || []).slice(0, 2);
    const eduItems = (data.education || []).slice(0, 1);
    const skillsList = (data.skills || []).slice(0, 5);

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
        height: '100%',
        opacity: isHovered ? 0.02 : 0.9,
        transition: 'opacity 0.2s ease',
        fontSize: '7px',
        lineHeight: 1.3,
        textAlign: 'left',
        color: isDark ? '#cbd5e1' : '#334155',
        overflow: 'hidden'
      }}>
        {/* Name and Header Block */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', borderBottom: `1px solid ${isDark ? '#27272a' : '#e2e8f0'}`, paddingBottom: '4px' }}>
          <div style={{ fontSize: '10px', fontWeight: 800, color: 'var(--brand)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {name}
          </div>
          <div style={{ fontSize: '7px', fontWeight: 600, color: isDark ? '#a1a1aa' : '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {title}
          </div>
        </div>

        {/* Work Experience Section */}
        {workItems.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <div style={{ fontSize: '6px', fontWeight: 800, color: isDark ? '#a1a1aa' : '#475569', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              Experience
            </div>
            {workItems.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '6px' }}>
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>
                    {item.company || 'Company'}
                  </span>
                  <span style={{ fontSize: '5px', color: isDark ? '#71717a' : '#94a3b8', flexShrink: 0 }}>
                    {item.startDate || item.start_date || ''} - {item.endDate || item.end_date || ''}
                  </span>
                </div>
                <div style={{ fontSize: '5.5px', color: isDark ? '#94a3b8' : '#64748b', fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.position || 'Role'}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Education Section */}
        {eduItems.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '2px' }}>
            <div style={{ fontSize: '6px', fontWeight: 800, color: isDark ? '#a1a1aa' : '#475569', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              Education
            </div>
            {eduItems.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '6px' }}>
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '130px' }}>
                    {item.school || item.institution || 'University'}
                  </span>
                  <span style={{ fontSize: '5px', color: isDark ? '#71717a' : '#94a3b8', flexShrink: 0 }}>
                    {item.startDate || item.start_date || item.endDate || item.end_date || ''}
                  </span>
                </div>
                <div style={{ fontSize: '5.5px', color: isDark ? '#94a3b8' : '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.degree || item.course || 'Degree'}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Skills Section */}
        {skillsList.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '2px' }}>
            <div style={{ fontSize: '6px', fontWeight: 800, color: isDark ? '#a1a1aa' : '#475569', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              Skills
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
              {skillsList.map((skill, idx) => (
                <span 
                  key={idx} 
                  style={{ 
                    fontSize: '5px', 
                    fontWeight: 600,
                    background: isDark ? '#27272a' : '#f1f5f9', 
                    padding: '1px 3px', 
                    borderRadius: '2px',
                    color: isDark ? '#cbd5e1' : '#475569'
                  }}
                >
                  {typeof skill === 'string' ? skill : (skill.name || '')}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Renders the mini Cover letter design outline layout preview card
  const renderMiniCoverLetterPreview = (cl, isHovered) => {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        width: '100%',
        height: '100%',
        opacity: isHovered ? 0.15 : 0.8,
        transition: 'opacity 0.2s ease',
        fontSize: '7px',
        lineHeight: 1.3,
        textAlign: 'left',
        color: isDark ? '#cbd5e1' : '#334155',
        overflow: 'hidden'
      }}>
        {/* Date / Sender simulation */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '4px' }}>
          <div style={{ height: '4px', width: '30%', background: 'var(--brand)', borderRadius: '1px' }} />
          <div style={{ height: '3px', width: '20%', background: '#cbd5e1', borderRadius: '1px' }} />
        </div>

        {/* Recipient block */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '6px', marginTop: '2px' }}>
          <div style={{ height: '3px', width: '40%', background: '#a1a1aa' }} />
          <div style={{ height: '3px', width: '35%', background: '#cbd5e1' }} />
        </div>

        {/* Letter content simulation */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5px' }}>
            <div style={{ height: '3px', width: '90%', background: '#cbd5e1' }} />
            <div style={{ height: '3px', width: '95%', background: '#cbd5e1' }} />
            <div style={{ height: '3px', width: '60%', background: '#cbd5e1' }} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5px', marginTop: '3px' }}>
            <div style={{ height: '3px', width: '95%', background: '#cbd5e1' }} />
            <div style={{ height: '3px', width: '85%', background: '#cbd5e1' }} />
            <div style={{ height: '3px', width: '40%', background: '#cbd5e1' }} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '6px' }}>
            <div style={{ height: '3px', width: '25%', background: '#cbd5e1' }} />
            <div style={{ height: '4px', width: '30%', background: 'var(--brand)', borderRadius: '1px', marginTop: '1.5px' }} />
          </div>
        </div>
      </div>
    );
  };

  const hasResumes = variants && variants.length > 0;

  return (
    <div style={{
      flex: 1,
      width: '100%',
      height: '100%',
      overflowY: 'auto',
      background: isDark ? '#09090b' : '#FAF9FC',
      color: isDark ? '#f4f4f5' : '#18181b',
      boxSizing: 'border-box',
      fontFamily: "'Inter', sans-serif"
    }}>
      {/* Hidden File Input for Resume Import */}
      <input
        type="file"
        ref={resumeFileInputRef}
        onChange={handleResumeUpload}
        accept=".pdf,.docx,.doc"
        style={{ display: 'none' }}
      />

      <div className="w-full max-w-[1040px] mx-auto px-6 sm:px-8 pt-8 pb-16 flex flex-col gap-6">
        {/* Welcome Section */}
        <div>
          {hasResumes ? (
            <>
              <h1 style={{
                fontSize: '28px',
                fontWeight: 800,
                margin: 0,
                color: isDark ? '#ffffff' : '#0f172a',
                fontFamily: "'Outfit', sans-serif",
                letterSpacing: '-0.5px'
              }}>
                Welcome back, {userName}! 👋
              </h1>
              <p style={{
                fontSize: '13px',
                color: isDark ? '#a1a1aa' : '#64748b',
                marginTop: '4px',
                fontWeight: 500
              }}>
                Select a document below to resume tailoring, check ATS keywords, or generate cover letters.
              </p>
            </>
          ) : (
            <>
              <h1 style={{
                fontSize: '28px',
                fontWeight: 800,
                margin: 0,
                color: isDark ? '#ffffff' : '#0f172a',
                fontFamily: "'Outfit', sans-serif",
                letterSpacing: '-0.5px'
              }}>
                Let's build your standout resume! 🚀
              </h1>
              <p style={{
                fontSize: '13px',
                color: isDark ? '#a1a1aa' : '#64748b',
                marginTop: '4px',
                fontWeight: 500
              }}>
                Create, optimize, and customize professional resumes and cover letters in seconds.
              </p>
            </>
          )}
        </div>

        {/* ── SECTION A: RENDER ACTIVE RESUMES & COVER LETTERS ONLY WHEN USER HAS RESUMES ── */}
        {hasResumes && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            
            {/* 1. Resumes Row */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="flex flex-col sm:flex-row sm:justify-between items-start sm:items-end gap-4 w-full">
                <div>
                  <h2 style={{
                    fontSize: '18px',
                    fontWeight: 800,
                    margin: 0,
                    color: isDark ? '#ffffff' : '#0f172a',
                    fontFamily: "'Outfit', sans-serif"
                  }}>
                    My Resumes
                  </h2>
                  <p style={{
                    fontSize: '12px',
                    color: isDark ? '#cbd5e1' : '#64748b',
                    marginTop: '2px',
                    fontWeight: 500
                  }}>
                    Optimize and update your career layouts.
                  </p>
                </div>

                {/* Search resumes and New Button */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 w-full sm:max-w-[420px]">
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Search
                      size={14}
                      style={{
                        position: 'absolute',
                        left: 10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--text-muted)'
                      }}
                    />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search resumes..."
                      style={{
                        width: '100%',
                        padding: '6px 12px 6px 30px',
                        background: isDark ? '#18181b' : '#ffffff',
                        border: `1px solid ${isDark ? '#27272a' : '#E5E7EB'}`,
                        borderRadius: '6px',
                        color: isDark ? '#ffffff' : '#0f172a',
                        fontSize: '12px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  {/* BUG: was handleStartAI which bypassed the create wizard. Now uses onCreateNew. */}
                  <button
                    onClick={() => onCreateNew?.('select')}
                    style={topActionBtnStyle(isDark)}
                  >
                    <Plus size={12} />
                    <span>New Resume</span>
                  </button>
                </div>
              </div>

              {/* Resume Previews Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(130px, 1fr))' : 'repeat(auto-fill, minmax(210px, 1fr))',
                gap: isMobile ? '12px' : '20px',
                marginTop: '4px'
              }}>
                {filteredVariants.map(v => {
                  const vId = v.id || v._variant_id || v.variantId;
                  const lastEdited = v.updatedAt || v.updated_at;
                  const isMaster = v.isMaster || v.is_master || v.id === 'master';
                  const isHovered = hoveredCardId === vId;

                  return (
                    <div
                      key={vId}
                      onMouseEnter={() => setHoveredCardId(vId)}
                      onMouseLeave={() => setHoveredCardId(null)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        position: 'relative'
                      }}
                    >
                      <div
                        style={{
                          height: isMobile ? '165px' : '240px',
                          background: isDark ? '#18181b' : '#ffffff',
                          border: `1px solid ${isHovered ? 'var(--brand)' : (isDark ? '#27272a' : '#e2e8f0')}`,
                          borderRadius: '12px',
                          boxShadow: isHovered 
                            ? '0 12px 20px rgba(252, 163, 17, 0.08)' 
                            : '0 2px 6px rgba(0, 0, 0, 0.02)',
                          padding: isMobile ? '10px' : '16px',
                          display: 'flex',
                          flexDirection: 'column',
                          cursor: 'pointer',
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.2s ease',
                          boxSizing: 'border-box'
                        }}
                        onClick={(e) => handleEditClick(vId, e)}
                      >
                        {/* Content render */}
                        {renderMiniResumePreview(v, isHovered)}

                        {isHovered && (
                          <div style={{
                            position: 'absolute',
                            inset: 0,
                            background: isDark ? 'rgba(11, 18, 32, 0.85)' : 'rgba(250, 250, 248, 0.85)',
                            backdropFilter: 'blur(4px)',
                            WebkitBackdropFilter: 'blur(4px)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '12px',
                            padding: isMobile ? '8px' : '16px',
                            boxSizing: 'border-box',
                            zIndex: 10
                          }}>
                            <button
                              onClick={(e) => handleContinueChat(vId, e)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: 'var(--brand)',
                                color: '#14213D',
                                border: 'none',
                                borderRadius: '8px',
                                padding: isMobile ? '5px 10px' : '8px 16px',
                                fontSize: isMobile ? '10px' : '12px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(252, 163, 17, 0.25)'
                              }}
                            >
                              <Sparkles size={13} />
                              <span>Continue</span>
                            </button>

                            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                              <button
                                onClick={(e) => handleTailorJD(vId, e)}
                                style={{ ...hoverIconBtnStyle(isDark), fontSize: '10px', padding: '5px 10px', gap: '4px', display: 'flex', alignItems: 'center', borderRadius: '7px' }}
                                title="Tailor for a Job Description"
                              >
                                <Briefcase size={12} />
                                <span style={{ fontWeight: 600, fontSize: '10px' }}>Tailor for Job</span>
                              </button>
                              <button
                                onClick={(e) => handleEditClick(vId, e)}
                                style={hoverIconBtnStyle(isDark)}
                                title="Edit in Editor"
                              >
                                <Edit2 size={13} />
                              </button>
                              <button
                                onClick={(e) => handleDuplicate(v, e)}
                                style={hoverIconBtnStyle(isDark)}
                                title="Duplicate"
                              >
                                <Copy size={13} />
                              </button>
                              {!isMaster && (
                                <button
                                  onClick={(e) => handleDeleteClick(vId, e)}
                                  style={{ ...hoverIconBtnStyle(isDark), color: 'var(--status-danger)' }}
                                  title="Delete"
                                >
                                  <Trash2 size={13} />
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', padding: '0 4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '12.5px', fontWeight: 700, color: isDark ? '#ffffff' : '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px' }}>
                            {v.name || 'Master Resume'}
                          </span>
                          {isMaster && (
                            <span style={{ fontSize: '8px', fontWeight: 800, color: '#10B981', background: 'rgba(16, 185, 129, 0.1)', padding: '1px 5px', borderRadius: '4px' }}>
                              Active
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: '10.5px', color: isDark ? '#71717a' : '#94a3b8' }}>
                          Modified {formatDate(lastEdited)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 2. Cover Letters Row */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="flex flex-col sm:flex-row sm:justify-between items-start sm:items-end gap-4 w-full">
                <div>
                  <h2 style={{
                    fontSize: '18px',
                    fontWeight: 800,
                    margin: 0,
                    color: isDark ? '#ffffff' : '#0f172a',
                    fontFamily: "'Outfit', sans-serif"
                  }}>
                    My Cover Letters
                  </h2>
                  <p style={{
                    fontSize: '12px',
                    color: isDark ? '#cbd5e1' : '#64748b',
                    marginTop: '2px',
                    fontWeight: 500
                  }}>
                    Tailor application letters for target job roles.
                  </p>
                </div>

                {/* BUG-S03 fix: was onTabChange('cover') which shows empty canvas. Now opens create wizard. */}
                <button
                  onClick={() => onCreateNew?.('cover_letter')}
                  style={topActionBtnStyle(isDark)}
                >
                  <Plus size={12} />
                  <span>New Cover Letter</span>
                </button>
              </div>

              {coverLetters && coverLetters.length > 0 ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(130px, 1fr))' : 'repeat(auto-fill, minmax(210px, 1fr))',
                  gap: isMobile ? '12px' : '20px',
                  marginTop: '4px'
                }}>
                  {coverLetters.map(cl => {
                    const clId = cl.id;
                    const isHovered = hoveredCoverId === clId;

                    return (
                      <div
                        key={clId}
                        onMouseEnter={() => setHoveredCoverId(clId)}
                        onMouseLeave={() => setHoveredCoverId(null)}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          position: 'relative'
                        }}
                      >
                        <div
                          style={{
                            height: isMobile ? '165px' : '240px',
                            background: isDark ? '#18181b' : '#ffffff',
                            border: `1px solid ${isHovered ? 'var(--brand)' : (isDark ? '#27272a' : '#e2e8f0')}`,
                            borderRadius: '12px',
                            boxShadow: isHovered 
                              ? '0 12px 20px rgba(14, 165, 233, 0.08)' 
                              : '0 2px 6px rgba(0, 0, 0, 0.02)',
                            padding: isMobile ? '10px' : '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'all 0.2s ease',
                            boxSizing: 'border-box'
                          }}
                          onClick={(e) => handleEditCoverLetter(clId, e)}
                        >
                          {/* Visual document card */}
                          {renderMiniCoverLetterPreview(cl, isHovered)}

                          {isHovered && (
                            <div style={{
                              position: 'absolute',
                              inset: 0,
                              background: isDark ? 'rgba(9, 9, 11, 0.75)' : 'rgba(255, 255, 255, 0.85)',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '12px',
                              padding: '16px',
                              boxSizing: 'border-box',
                              zIndex: 10
                            }}>
                              <button
                                onClick={(e) => handleEditCoverLetter(clId, e)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  background: 'var(--brand)',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '8px 16px',
                                  fontSize: '12px',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  boxShadow: '0 4px 12px rgba(14, 165, 233, 0.2)'
                                }}
                              >
                                <Edit2 size={13} />
                                <span>Edit Letter</span>
                              </button>

                              <button
                                onClick={(e) => handleDeleteCoverLetter(clId, e)}
                                style={{ ...hoverIconBtnStyle(isDark), color: 'var(--status-danger)', marginTop: '4px' }}
                                title="Delete"
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          )}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', padding: '0 4px' }}>
                          <span style={{ fontSize: '12.5px', fontWeight: 700, color: isDark ? '#ffffff' : '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {cl.name || 'Cover Letter'}
                          </span>
                          <span style={{ fontSize: '10.5px', color: isDark ? '#71717a' : '#94a3b8' }}>
                            Modified {formatDate(cl.updatedAt || cl.updated_at)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* EMPTY STATE FOR COVER LETTERS */
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '36px 20px',
                  background: isDark ? '#121214' : '#FFFFFF',
                  border: `1px solid ${isDark ? '#27272a' : '#E5E7EB'}`,
                  borderRadius: '12px',
                  textAlign: 'center',
                  gap: '8px'
                }}>
                  <span style={{ fontSize: '12.5px', fontWeight: 700, color: isDark ? '#ffffff' : '#0f172a' }}>
                    No cover letters created yet
                  </span>
                  <span style={{ fontSize: '11px', color: isDark ? '#71717a' : '#64748b' }}>
                    Click "+ New Cover Letter" to build your first letter.
                  </span>
                </div>
              )}
            </div>

          </div>
        )}

        {/* ── SECTION B: HERO BANNER & 3 FEATURE CARDS - DISPLAY ONLY WHEN USER HAS NO RESUMES ── */}
        {!hasResumes && (
          <>
            {/* Hero Banner Section */}
            <div style={{
              position: 'relative',
              background: isDark 
                ? 'linear-gradient(135deg, rgba(14, 165, 233, 0.15) 0%, rgba(15, 23, 42, 0.3) 100%)' 
                : 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)',
              borderRadius: '16px',
              border: `1px solid ${isDark ? 'rgba(14, 165, 233, 0.2)' : 'rgba(14, 165, 233, 0.08)'}`,
              padding: '24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              overflow: 'hidden'
            }}>
              <div style={{ zIndex: 2, flex: 1, maxWidth: '640px' }}>
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: 800,
                  color: isDark ? '#ffffff' : '#1e1b4b',
                  margin: 0,
                  fontFamily: "'Outfit', sans-serif"
                }}>
                  Craft Resume for Every Job, Your Way!
                </h2>
                <p style={{
                  fontSize: '13px',
                  color: isDark ? '#cbd5e1' : '#0284c7',
                  marginTop: '4px',
                  fontWeight: 500
                }}>
                  Smarter, faster, and tailored to you — however you start.
                </p>

                {/* Action buttons row */}
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px',
                  marginTop: '20px'
                }}>
                  <button
                    onClick={handleStartAI}
                    disabled={startingAI}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: startingAI ? '#64748b' : 'var(--brand)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '9999px',
                      padding: '8px 16px',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: startingAI ? 'wait' : 'pointer',
                      boxShadow: '0 2px 8px rgba(14, 165, 233, 0.25)',
                      transition: 'all 0.15s ease',
                      opacity: startingAI ? 0.7 : 1,
                    }}
                    onMouseEnter={e => !startingAI && (e.currentTarget.style.background = 'var(--brand-hover)')}
                    onMouseLeave={e => !startingAI && (e.currentTarget.style.background = 'var(--brand)')}
                  >
                    <Sparkles size={13} fill="white" />
                    <span>{startingAI ? 'Analyzing profile…' : 'Build from Profile'}</span>
                  </button>

                  <button
                    onClick={handleStartScratch}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: '#FFFFFF',
                      color: '#1e1b4b',
                      border: '1px solid #E5E7EB',
                      borderRadius: '9999px',
                      padding: '8px 16px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--brand)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#E5E7EB'}
                  >
                    <FileText size={13} style={{ color: '#4b5563' }} />
                    <span>Start from Scratch</span>
                  </button>

                  {/* BUG-S06 fix: LinkedIn import not implemented. Button disabled with tooltip. */}
                  <button
                    title="Coming soon — LinkedIn import is not yet available"
                    disabled
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: isDark ? '#27272a' : '#f1f5f9',
                      color: isDark ? '#52525b' : '#94a3b8',
                      border: '1px solid transparent',
                      borderRadius: '9999px',
                      padding: '8px 16px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'not-allowed',
                      opacity: 0.6,
                    }}
                  >
                    <Linkedin size={13} style={{ color: '#94a3b8' }} />
                    <span>LinkedIn (Coming Soon)</span>
                  </button>

                  {/* BUG-S05 fix: was directly clicking file input, bypassing ImportChoiceModal */}
                  <button
                    onClick={() => onImportResume?.()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: '#FFFFFF',
                      color: '#1e1b4b',
                      border: '1px solid #E5E7EB',
                      borderRadius: '9999px',
                      padding: '8px 16px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--brand)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#E5E7EB'}
                  >
                    <Upload size={13} style={{ color: '#4b5563' }} />
                    <span>Upload Resume</span>
                  </button>
                </div>
              </div>

              {/* Floating graphic decorations on the right */}
              <div className="hidden md:flex" style={{
                alignItems: 'center',
                gap: '16px',
                marginRight: '12px',
                opacity: 0.85,
                pointerEvents: 'none',
                userSelect: 'none'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: '#FFFFFF',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transform: 'rotate(-8deg)'
                }}>
                  <Briefcase size={20} style={{ color: '#854d0e' }} />
                </div>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: '#FFFFFF',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transform: 'rotate(5deg)'
                }}>
                  <Linkedin size={20} style={{ color: '#0077b5' }} />
                </div>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: '#FFFFFF',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transform: 'rotate(-3deg)'
                }}>
                  <Chrome size={20} style={{ color: '#db4437' }} />
                </div>
              </div>
            </div>

            {/* 3 Core Services Features Cards Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))',
              gap: '16px',
              marginTop: '8px'
            }}>
              {/* BUG-S12 fix: was sending user with no resume to JD matcher. Now opens create wizard. */}
              <div
                onClick={() => onCreateNew?.('job_details')}
                style={cardStyle(isDark)}
                onMouseEnter={e => cardHover(e, isDark)}
                onMouseLeave={e => cardLeave(e, isDark)}
              >
                <div style={cardHeaderStyle}>
                  <div style={cardIconWrapperStyle('rgba(14, 165, 233, 0.1)', 'var(--brand)')}>
                    <FileText size={18} />
                  </div>
                  <h3 style={cardTitleStyle(isDark)}>Build Tailored Resume</h3>
                </div>
                <p style={cardDescStyle(isDark)}>
                  Tell the AI what role you're targeting. It will ask about your background and build a tailored resume in minutes.
                </p>
              </div>

              {/* BUG-S11 fix: ?tab=ats query param was never read. Now passes state to navigate. */}
              <div
                onClick={() => {
                  const path = mostRecentDoc
                    ? `/app/resume/${mostRecentDoc.id}/chat`
                    : '/app/resume/master/chat';
                  navigate(path, { state: { openPanel: 'ats' } });
                }}
                style={cardStyle(isDark)}
                onMouseEnter={e => cardHover(e, isDark)}
                onMouseLeave={e => cardLeave(e, isDark)}
              >
                <div style={cardHeaderStyle}>
                  <div style={cardIconWrapperStyle('rgba(14, 165, 233, 0.1)', 'var(--brand)')}>
                    <Compass size={18} />
                  </div>
                  <h3 style={cardTitleStyle(isDark)}>Resume ATS Score Checker</h3>
                </div>
                <p style={cardDescStyle(isDark)}>
                  Analyze your resume against ATS criteria, check your score, and get actionable suggestions for structure and text.
                </p>
              </div>

              {/* BUG-S03 fix: was onTabChange('cover') which shows empty canvas. Now opens create wizard. */}
              <div
                onClick={() => onCreateNew?.('cover_letter')}
                style={cardStyle(isDark)}
                onMouseEnter={e => cardHover(e, isDark)}
                onMouseLeave={e => cardLeave(e, isDark)}
              >
                <div style={cardHeaderStyle}>
                  <div style={cardIconWrapperStyle('rgba(16, 185, 129, 0.1)', '#059669')}>
                    <Mail size={18} />
                  </div>
                  <h3 style={cardTitleStyle(isDark)}>Cover Letter Builder</h3>
                </div>
                <p style={cardDescStyle(isDark)}>
                  Create highly-personalized, job-specific cover letters customized to your work experience and target role.
                </p>
              </div>
            </div>

            {/* 4. Empty State Placeholder for Resumes at Bottom */}
            <div style={{
              marginTop: '32px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              <h2 style={{
                fontSize: '18px',
                fontWeight: 800,
                margin: 0,
                color: isDark ? '#ffffff' : '#0f172a',
                fontFamily: "'Outfit', sans-serif"
              }}>
                My Resumes
              </h2>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px 24px',
                background: isDark ? '#121214' : '#FFFFFF',
                border: `1px solid ${isDark ? '#27272a' : '#E5E7EB'}`,
                borderRadius: '16px',
                textAlign: 'center',
                gap: '16px'
              }}>
                <button
                  onClick={handleStartAI}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'transparent',
                    color: 'var(--brand)',
                    border: '1.5px solid var(--brand)',
                    borderRadius: '8px',
                    padding: '8px 18px',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'var(--brand-light)';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <Plus size={14} strokeWidth={2.5} />
                  <span>New Resume</span>
                </button>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 800, margin: 0, color: isDark ? '#ffffff' : '#0f172a' }}>
                    No resumes found
                  </h3>
                  <p style={{ fontSize: '12px', margin: 0, color: isDark ? '#71717a' : '#64748b' }}>
                    Get started by creating a new resume.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* Redesign styles helpers */
const cardStyle = (isDark) => ({
  background: isDark ? '#18181b' : '#FFFFFF',
  border: `1px solid ${isDark ? '#27272a' : '#E5E7EB'}`,
  borderRadius: '16px',
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
});

const cardHover = (e, isDark) => {
  e.currentTarget.style.borderColor = 'var(--brand)';
  e.currentTarget.style.transform = 'translateY(-2px)';
  e.currentTarget.style.boxShadow = isDark 
    ? '0 12px 24px rgba(0,0,0,0.4)' 
    : '0 12px 24px rgba(14, 165, 233, 0.06)';
};

const cardLeave = (e, isDark) => {
  e.currentTarget.style.borderColor = isDark ? '#27272a' : '#E5E7EB';
  e.currentTarget.style.transform = 'translateY(0)';
  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)';
};

const cardHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
};

const cardIconWrapperStyle = (bg, color) => ({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  background: bg,
  color: color,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
});

const cardTitleStyle = (isDark) => ({
  fontSize: '14px',
  fontWeight: 700,
  margin: 0,
  color: isDark ? '#ffffff' : '#1e293b'
});

const cardDescStyle = (isDark) => ({
  fontSize: '12px',
  lineHeight: 1.5,
  margin: '4px 0 0',
  color: isDark ? '#9ca3af' : '#64748b'
});

const hoverIconBtnStyle = (isDark) => ({
  background: isDark ? '#1F1F24' : '#F3F4F6',
  border: 'none',
  padding: 8,
  color: isDark ? '#cbd5e1' : '#4b5563',
  cursor: 'pointer',
  borderRadius: '6px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.12s ease'
});

const topActionBtnStyle = (isDark) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  background: 'var(--brand)',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '6px',
  padding: '6px 12px',
  fontSize: '12px',
  fontWeight: 700,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  boxShadow: '0 2px 8px rgba(14, 165, 233, 0.2)'
});
